import type { ProductDetailRepository, ProductDetailParams } from '../../domain/interfaces/ProductDetailRepository';
import type { CustomizationRepository, CustomizationParams } from '@/features/customization/domain/interfaces/CustomizationRepository';
import type { DeliveryDateRepository } from '../../domain/interfaces/DeliveryDateRepository';
import type { PdpStoreRepository } from '../../domain/interfaces/PdpStoreRepository';
import type { PdpNearbyStore } from '../../domain/models/PdpNearbyStore';
import type { WishlistRepository } from '@/features/wishlist/domain/interfaces/WishlistRepository';
import type { VideoCallRepository } from '@/features/video-call/domain/interfaces/VideoCallRepository';
import type { ProductDetail } from '../../domain/models/ProductDetail';
import type { ProductCustomization } from '@/features/customization/domain/models/ProductCustomization';
import type { DeliveryInfo } from '../../domain/models/DeliveryInfo';
import type { PdpDeliveryContext } from '../../domain/models/PdpDeliveryContext';
import type { WishlistState } from '@/features/wishlist/domain/models/WishlistItem';
import type { VideoCallCartState } from '@/features/video-call/domain/models/VideoCallCart';
import type { PdpEddProductSpecs } from '../../domain/models/ResolvedExpectedDeliveryDate';
import type { SimilarDesignsRepository } from '../../domain/interfaces/SimilarDesignsRepository';
import type { SimilarDesigns } from '../../domain/models/SimilarDesigns';
import { resolveExpectedDeliveryDate } from '../../utils/resolveExpectedDeliveryDate';
import { toPdpDeliveryAppVm, type PdpDeliveryAppVm } from '../viewModels/pdpDelivery.app.vm';

export type ProductDetailService = ReturnType<typeof createProductDetailService>;

export interface GetPdpDeliveryContextParams {
  pincode: string;
  designId: string;
  customizationId: number;
  productSpecs: PdpEddProductSpecs;
  selectedSize: string | null;
}

export function createProductDetailService(
  detailRepo: ProductDetailRepository,
  customizationRepo: CustomizationRepository,
  deliveryRepo: DeliveryDateRepository,
  storeRepo: PdpStoreRepository,
  wishlistRepo?: WishlistRepository,
  videoCallRepo?: VideoCallRepository,
  similarDesignsRepo?: SimilarDesignsRepository
) {
  return {
    async getProductDetail(designId: string, params?: ProductDetailParams): Promise<ProductDetail> {
      return detailRepo.fetchProductDetail(designId, params);
    },

    async applyCustomization(designId: string, params: CustomizationParams): Promise<ProductCustomization> {
      return customizationRepo.fetchCustomization(designId, params);
    },

    async getDeliveryDate(pincode: string, designId: string): Promise<DeliveryInfo> {
      return deliveryRepo.fetchDeliveryDate(pincode, designId);
    },

    async getPdpDeliveryContext(params: GetPdpDeliveryContextParams): Promise<PdpDeliveryContext> {
      const { pincode, designId, customizationId, productSpecs, selectedSize } = params;

      const resolvedPincode = pincode && pincode.length === 6 ? pincode : '';
      const deliveryInfo = await deliveryRepo.fetchDeliveryDate(resolvedPincode, designId);

      let storeForCustomization = null;
      let nearbyStores: Record<string, PdpNearbyStore> = {};

      if (resolvedPincode) {
        const [storeForCustomizationResult, nearbyStoresResult] = await Promise.allSettled([
          storeRepo.fetchStoreForCustomization(resolvedPincode, customizationId),
          storeRepo.fetchNearbyStores(resolvedPincode),
        ]);

        storeForCustomization =
          storeForCustomizationResult.status === 'fulfilled'
            ? storeForCustomizationResult.value
            : null;
        nearbyStores =
          nearbyStoresResult.status === 'fulfilled' ? nearbyStoresResult.value : {};
      }

      const resolvedEdd = resolveExpectedDeliveryDate(
        deliveryInfo.designEddDetails,
        productSpecs,
        selectedSize
      );

      return {
        deliveryInfo,
        resolvedEdd,
        storeForCustomization,
        nearbyStores,
      };
    },

    async getPdpDeliveryAppVm(params: GetPdpDeliveryContextParams): Promise<PdpDeliveryAppVm> {
      const context = await this.getPdpDeliveryContext(params);
      return toPdpDeliveryAppVm(context);
    },

    async addToWishlist(designId: number): Promise<WishlistState | null> {
      if (!wishlistRepo) return null;
      return wishlistRepo.addToWishlist(designId);
    },

    async removeFromWishlist(designId: number): Promise<WishlistState | null> {
      if (!wishlistRepo) return null;
      return wishlistRepo.removeFromWishlist(designId);
    },

    async addToVideoCallCart(customizationId: number): Promise<VideoCallCartState | null> {
      if (!videoCallRepo) return null;
      return videoCallRepo.addToVideoCallCart(customizationId);
    },

    async getSimilarDesigns(designId: string): Promise<SimilarDesigns | null> {
      if (!similarDesignsRepo) return null;
      return similarDesignsRepo.fetchSimilarDesigns(designId);
    },
  };
}
